import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ExerciseAction, PositionCoords } from '@textbook-api-client';
import { BehaviorSubject, Observable, of, OperatorFunction } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import {
  TextbookActionsHandler,
  TextbookActionsHandlerToken,
} from '../../../external/providers/textbook-actions-handler';
import { GroupInfo } from '../../models';
import { TextbookActionBase } from '../@base/textbook-action-base.model';
import { ModalSize } from '../@base/models';
import { ModalPositionConfig } from '../@base/models/modal-position-config';
import { CSS_SIZE_UNIT_PX } from '../../../external/models';
import { ActionDrawerApi } from '../@base/models/action-drawer-api';
import { MAX_PERCENT } from '../../../external/models/consts';

enum ButtonState {
  All = 'Wszystkie przykłady',
  Single = 'Pojedyncze przykłady',
}

enum ModalState {
  Answer,
  Tip,
}

@Component({
  selector: 'txtb-exercise',
  templateUrl: './exercise.component.html',
  styleUrls: ['./exercise.component.scss'],
  standalone: false,
})
export class ExerciseComponent
  extends TextbookActionBase<ExerciseAction>
  implements AfterViewInit, OnInit, OnDestroy
{
  @Input() groupInfo!: GroupInfo;
  @ViewChild('img') bigImg!: ElementRef<HTMLImageElement>;
  @ViewChild('wrapper') wrapper!: ElementRef<HTMLDivElement>;

  subPointsShown = false;
  subSectionLength = 0;
  activeElement = 1;
  modalOpened = false;
  buttonState = ButtonState.All;
  modalState = ModalState.Answer;
  actionContentCollapsed = false;
  readonly MAX_PERCENT = MAX_PERCENT;
  readonly answerTitle = 'Odpowiedź';
  readonly defaultTipTitle = 'Podpowiedź';
  readonly modalCompactSize = ModalSize.COMPACT;
  private actionDrawerApi?: ActionDrawerApi;
  private resizeObserver: ResizeObserver | null = null;
  private drawerActionWrapper: HTMLElement | null = null;
  private getBlobAsString: OperatorFunction<string | undefined, string> = switchMap((id) =>
    id ? this.textbookActionsHandler.getResourceBlobAsString(id) : of('')
  );
  private readonly questionSubject = new BehaviorSubject<ExerciseAction['meta']['question'] | null>(
    null
  );
  private readonly answerBS = new BehaviorSubject<ExerciseAction['meta']['answer']>(null);
  private readonly tipBS = new BehaviorSubject<ExerciseAction['meta']['tip']>(null);
  private readonly drawerActionWrapperResized = new BehaviorSubject<void>(void 0);
  readonly question$ = this.questionSubject.asObservable();
  readonly questionImage$ = this.question$.pipe(
    map((question) => question?.resourceId),
    switchMap((resourceId) =>
      !!resourceId ? this.textbookActionsHandler.getResourceBlobAsString(resourceId) : of(null)
    )
  );
  readonly horizontalScroll$ = this.question$.pipe(map((question) => !!question?.horizontalScroll));
  readonly verticalScroll$ = this.question$.pipe(map((question) => !!question?.verticalScroll));

  readonly answerImageUrl$ = this.answerBS
    .asObservable()
    .pipe(map((answer) => answer?.image?.resourceId));
  readonly answerImage$ = this.answerImageUrl$.pipe(this.getBlobAsString);
  readonly answer$ = this.answerBS.asObservable();
  readonly tipImageUrl$ = this.tipBS.asObservable().pipe(map((tip) => tip?.image?.resourceId));
  readonly tipImage$ = this.tipImageUrl$.pipe(this.getBlobAsString);
  readonly tipTitle$ = this.tipBS
    .asObservable()
    .pipe(map((tip) => tip?.title || this.defaultTipTitle));
  readonly tip$ = this.tipBS.asObservable();

  readonly answerModalPositionConfig$: Observable<ModalPositionConfig | null> = this.answerBS
    .asObservable()
    .pipe(map((answer) => this.toPositionConfig(answer?.position)));

  readonly tipModalPositionConfig$: Observable<ModalPositionConfig | null> = this.tipBS
    .asObservable()
    .pipe(map((tip) => this.toPositionConfig(tip?.position)));

  readonly modalBgPositionConfig$: Observable<ModalPositionConfig | null> =
    this.drawerActionWrapperResized.asObservable().pipe(
      debounceTime(100),
      map(() => {
        const rect = this.drawerActionWrapper?.getBoundingClientRect();
        if (!rect) {
          return null;
        }
        return {
          left: `${rect.left}${CSS_SIZE_UNIT_PX}`,
          top: `${rect.top}${CSS_SIZE_UNIT_PX}`,
          width: `${rect.width}${CSS_SIZE_UNIT_PX}`,
          height: `${rect.height}${CSS_SIZE_UNIT_PX}`,
        };
      })
    );

  constructor(
    @Inject(TextbookActionsHandlerToken)
    public readonly textbookActionsHandler: TextbookActionsHandler,
    public readonly ref: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone
  ) {
    super();
  }

  // tslint:disable-next-line:variable-name
  @Input('action') set action(action: ExerciseAction) {
    this._action = action;
    this.subSectionLength = this._action?.meta?.subPoints?.length || 0;
    this.subPointsShown = !this._action?.meta?.question;
    this.buttonState = ButtonState.Single;
    this.modalOpened = false;
    this.dataUpdate();
  }

  get action(): ExerciseAction {
    return this._action;
  }

  public ngOnInit(): void {
    this.subPointsShown = !this.action?.meta?.question;
    this.buttonState = ButtonState.Single;
  }

  public ngAfterViewInit(): void {
    this.subPointsShown = !this.action?.meta?.question;
    this.subSectionLength = this.action?.meta?.subPoints?.length || 0;
    this.dataUpdate();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  changeElement(e: number): void {
    this.activeElement = e;
    setTimeout(() => this.collapse(false), 200);
    this.dataUpdate();
    this.closeModal();
    this.scrollToTop();
    this.actionDrawerApi?.resetZoom();
    this.actionDrawerApi?.enableZoom();
  }

  scrollToTop(): void {
    this.wrapper.nativeElement.scroll(0, 0);
  }

  contentCollapse(state: boolean): void {
    setTimeout(() => this.collapse(state), 200);
  }

  collapse(state: boolean): void {
    this.actionContentCollapsed = state;
    this.ref.detectChanges();
  }

  onActionContentWrapperRendered(element: HTMLElement | null): void {
    this.resizeObserver?.disconnect();
    this.drawerActionWrapper = element;
    if (!element) {
      return;
    }
    this.resizeObserver = new ResizeObserver(() =>
      this.zone.runOutsideAngular(() => {
        this.drawerActionWrapperResized.next();
      })
    );
    this.resizeObserver.observe(element);
  }

  onLoaded(): void {
    this.bigImg?.nativeElement &&
      this.renderer.setStyle(this.bigImg.nativeElement, 'transform', 'translateZ(0)');
  }

  onActionDrawerApiInitialized(service: ActionDrawerApi): void {
    this.actionDrawerApi = service;
  }

  switchScreen(): void {
    this.subPointsShown = !this.subPointsShown;
    this.buttonState = this.subPointsShown ? ButtonState.All : ButtonState.Single;
    this.dataUpdate();
    this.closeModal();
    this.activeElement = 1;
    setTimeout(() => this.collapse(false), 200);
    this.actionDrawerApi?.resetZoom();
    this.actionDrawerApi?.enableZoom();
  }

  dataUpdate(): void {
    this.subPointsShown ? this.subPointDataUpdate() : this.mainPointDataUpdate();
  }

  subPointDataUpdate(): void {
    const subPoint = this.action?.meta?.subPoints;
    const activeSubPoint = subPoint && subPoint[this.activeElement - 1];
    this.answerBS.next(activeSubPoint?.answer || null);
    this.tipBS.next(activeSubPoint?.tip || null);
    this.questionSubject.next(activeSubPoint ? activeSubPoint.question : null);
  }

  mainPointDataUpdate(): void {
    this.answerBS.next(this.action?.meta?.answer || null);
    this.tipBS.next(this.action?.meta?.tip || null);
    this.questionSubject.next(this.action?.meta?.question);
  }

  closeModal(): void {
    this.modalOpened = false;
    this.actionDrawerApi?.enableZoom();
  }

  toggleModal(state: ModalState): void {
    if (!this.modalOpened) {
      this.actionDrawerApi?.resetZoom();
      this.actionDrawerApi?.disableZoom();
    } else {
      this.actionDrawerApi?.enableZoom();
    }
    this.modalOpened = !this.modalOpened;
    this.modalState = state;
    this.dataUpdate();
  }

  private toPositionConfig(position?: PositionCoords | null): ModalPositionConfig | null {
    if (!position) {
      return null;
    }
    return {
      left: `${position.left}%`,
      top: `${position.top}%`,
      width: `${position.width}%`,
      height: `${position.height}%`,
    };
  }
}
